import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Account,
  AccountTypeEnum,
  GroupedAccounts,
  TransferOptions,
} from '@varo/graphql-schema-types';
import { router, useLocalSearchParams } from 'expo-router';
import { TransferInput } from 'features/moveMoney/screens/transfer/TransferMoney/TransferMoney';
import { Dispatch, RefObject, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLeadingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

export interface AccountListBottomSheetProps {
  bottomSheetRef: RefObject<BottomSheetModal | null>;
  showingType: 'from' | 'to' | undefined;
  setSelectedFromAccount: Dispatch<SetStateAction<Account>>;
  setSelectedToAccount: Dispatch<SetStateAction<Account | undefined>>;
  setTransferInput: Dispatch<SetStateAction<TransferInput>>;
  fetching: boolean;
  sourceData: GroupedAccounts;
  destinationData: GroupedAccounts;
  refreshSourceAccounts: () => void;
  refreshDestinationAccounts: () => void;
  onLinkAccountPress: () => void;
  setTransferOptions: Dispatch<SetStateAction<TransferOptions | undefined>>;
}

interface ExternalInternalAccountsListProps {
  accounts: Account[];
  isFromAccountsVisible: boolean;
  handleAccountSelect: (account: Account) => void;
  listItemLeadingAccessory: (account: Account) => ListItemLeadingAccessoryProps;
  listItemTrailingAccessory: (
    account: Account,
  ) => ListItemTrailingAccessoryProps | null;
}

const BottomSheetAccountsList = ({
  accounts,
  isFromAccountsVisible,
  handleAccountSelect,
  listItemLeadingAccessory,
  listItemTrailingAccessory,
}: ExternalInternalAccountsListProps) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });

  if (!accounts?.length) {
    return null;
  }

  return accounts.map((account) => {
    if (account.type === AccountTypeEnum.External) {
      return (
        <View key={account?.id}>
          <AmplitudeMask style={styles.amplitudeMask}>
            <ListItem
              primaryCopy={`${account?.institution?.name}  ${toTitleCase(account?.product)} • ${account?.accountNumberLast4}`}
              onPress={() => handleAccountSelect(account)}
              secondaryCopy={
                account?.availableBalance?.formatted
                  ? `${translatedText('available')} ${account?.availableBalance?.formatted}`
                  : null
              }
              showsChevron={false}
              leadingAccessory={listItemLeadingAccessory(account)}
              trailingAccessory={listItemTrailingAccessory(account)}
              analyticsId="bottomSheetAccountSelect"
              customAttributes={{
                direction: isFromAccountsVisible ? 'from' : 'to',
                account_type: account?.type?.toLowerCase(),
                institution_name: account?.institution?.name?.toLowerCase(),
              }}
            />
          </AmplitudeMask>
          <View style={styles.divider}>
            <HairLineDivider />
          </View>
        </View>
      );
    }

    return (
      <AmplitudeMask
        key={account?.id}
        blockStyleMask
        style={styles.amplitudeMask}
      >
        <View>
          <ListItem
            primaryCopy={`${account?.nickname} • ${account?.accountNumberLast4}`}
            onPress={() => handleAccountSelect(account)}
            secondaryCopy={`${translatedText('available')} ${account?.availableBalance?.formatted}`}
            showsChevron={false}
            leadingAccessory={listItemLeadingAccessory(account)}
            trailingAccessory={listItemTrailingAccessory(account)}
            isDisabled={
              isFromAccountsVisible &&
              Number(account?.availableBalance?.amount) <= 0
            }
            analyticsId="bottomSheetAccountSelect"
            customAttributes={{
              direction: isFromAccountsVisible ? 'from' : 'to',
              account_type:
                account?.type === AccountTypeEnum.Internal
                  ? account?.product?.toLowerCase()
                  : account?.type?.toLowerCase(),
              institution_name: account?.institution?.name?.toLowerCase(),
            }}
          />
          <View style={styles.divider}>
            <HairLineDivider />
          </View>
        </View>
      </AmplitudeMask>
    );
  });
};

const AccountSectionTitle = ({
  type,
  isVisible,
}: {
  type: AccountTypeEnum;
  isVisible: boolean;
}) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });

  if (!isVisible) {
    return null;
  } else if (type === AccountTypeEnum.External) {
    return (
      <BodySmBoldText style={styles.sectionTitle} numberOfLines={1}>
        {translatedText('externalAccounts')}
      </BodySmBoldText>
    );
  }

  // Else return "Internal accounts" title
  return (
    <BodySmBoldText numberOfLines={0} style={styles.sectionTitle}>
      {translatedText('varoAccounts')}
    </BodySmBoldText>
  );
};

const AccountListBottomSheet = ({
  bottomSheetRef,
  showingType,
  setSelectedFromAccount,
  setSelectedToAccount,
  setTransferInput,
  fetching,
  sourceData,
  destinationData,
  refreshSourceAccounts,
  refreshDestinationAccounts,
  onLinkAccountPress,
  setTransferOptions,
}: AccountListBottomSheetProps) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });

  const { refreshAccounts } = useLocalSearchParams();

  const flow = useVaroOnboardingFlow();

  const isFromAccountsVisible = showingType === 'from';

  useEffect(() => {
    if (refreshAccounts) {
      if (isFromAccountsVisible) {
        refreshSourceAccounts();
      } else {
        refreshDestinationAccounts();
      }

      bottomSheetRef.current?.dismiss();
    }
  }, [
    bottomSheetRef,
    refreshAccounts,
    refreshDestinationAccounts,
    refreshSourceAccounts,
    isFromAccountsVisible,
  ]);

  const handleAccountSelect = (account: Account) => {
    const action = account.transferInfo?.action;
    if (action) {
      router.navigate({
        pathname: `/${flow}/transfer/external-account-error`,
        params: {
          accountId: account.id,
          actionType: action.type,
          actionCta: action.cta,
          actionTitle: action.title,
          actionMessage: action.message,
        },
      });
      bottomSheetRef.current?.dismiss();
      return;
    }
    if (isFromAccountsVisible) {
      setSelectedFromAccount(account);
      setSelectedToAccount(undefined);
      setTransferOptions(undefined);
      setTransferInput((prevState) => {
        return {
          ...prevState,
          sourceAccountId: account?.id,
          destinationAccountId: '',
        };
      });
    } else if (showingType === 'to') {
      setSelectedToAccount(account);
      setTransferInput((prevState) => {
        return {
          ...prevState,
          destinationAccountId: account?.id,
        };
      });
    }
    bottomSheetRef?.current?.dismiss();
  };

  const handleLinkAnAccountClick = () => {
    onLinkAccountPress();
    bottomSheetRef.current?.dismiss();
  };

  const listItemLeadingAccessory = (
    account: Account,
  ): ListItemLeadingAccessoryProps => ({
    id: VXLComponentId.ICON_AVATAR,
    name: VXLComponentId.ICON_AVATAR,
    props: {
      variant: 'MD',
      imageSource: { uri: account?.iconUrl },
    },
  });

  const listItemTrailingAccessory = (
    account: Account,
  ): ListItemTrailingAccessoryProps | null =>
    account.tag
      ? {
          id: VXLComponentId.TAG,
          name: VXLComponentId.TAG,
          props: {
            text: account.tag.text,
            type: TagType.WARNING,
          },
        }
      : null;

  const externalAccountsToMap: Account[] = isFromAccountsVisible
    ? sourceData?.external
    : destinationData?.external;

  return (
    <View style={styles.container}>
      <BottomSheet ref={bottomSheetRef} allowScrolling>
        {fetching ? (
          <View style={styles.loaderContainer}>
            <LoadingSpinner />
          </View>
        ) : (
          <>
            <TitleMdText style={styles.title} numberOfLines={1}>
              {translatedText(
                isFromAccountsVisible ? 'transferFrom' : 'transferTo',
              )}
            </TitleMdText>
            <AccountSectionTitle
              type={AccountTypeEnum.External}
              isVisible={
                isFromAccountsVisible
                  ? sourceData?.external?.length > 0
                  : destinationData?.external?.length > 0
              }
            />
            <BottomSheetAccountsList
              accounts={externalAccountsToMap}
              isFromAccountsVisible={isFromAccountsVisible}
              handleAccountSelect={handleAccountSelect}
              listItemLeadingAccessory={listItemLeadingAccessory}
              listItemTrailingAccessory={listItemTrailingAccessory}
            />
            <AccountSectionTitle
              type={AccountTypeEnum.Internal}
              isVisible={
                sourceData?.internal?.length > 0 ||
                destinationData?.internal?.length > 0
              }
            />
            <BottomSheetAccountsList
              accounts={
                isFromAccountsVisible
                  ? sourceData?.internal
                  : destinationData?.internal
              }
              isFromAccountsVisible={isFromAccountsVisible}
              handleAccountSelect={handleAccountSelect}
              listItemLeadingAccessory={listItemLeadingAccessory}
              listItemTrailingAccessory={listItemTrailingAccessory}
            />

            <ListItem
              primaryCopy={translatedText('linkAnAccount')}
              onPress={handleLinkAnAccountClick}
              leadingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconSize: 'MD',
                  iconKey: 'MONEY',
                  iconAccessibilityLabel: 'money-icon',
                },
              }}
              isDisabled={false}
              analyticsId="linkExternalAccount"
              customAttributes={{ cta: 'Link an account' }}
            />
          </>
        )}
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  amplitudeMask: {
    flex: 1,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  loaderContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    paddingVertical: theme.spacing.m,
  },
  sectionTitle: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  title: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default AccountListBottomSheet;
